import { fetchAuthorizedBinary, get } from '../../../api/client';
import type { OrderFilters } from '../../../types';
import { downloadBytes } from '../../../utils/download';
import { todayString } from '../../../utils/dateUtils';
import { toOrderStatusQuery } from '../../../utils/orderStatusFilter';
import { printHtmlDocument } from '../../../utils/printHtml';
import { XLSX_MIME } from '../../../utils/xlsx';
import type { ExportPeriod } from './exportPeriod';

export interface ExportOptions {
  /** 按客户ID筛选 */
  customerId?: string;
  /** 客户名称（用于文件名） */
  customerName?: string;
  /**
   * 导出面板接管的日期区间（按月 / 按季 / 按年的某一一期，或自定义档填出来的两头）。
   * 给了就整体替换筛选里的日期区间，并把 `label` 一并带进文件名——
   * `订单列表_2026年三季度_2026-10-10.xlsx`；否则同一批导出的几个文件在下载目录里只看名字分不出各是哪期的账。
   * 自定义档两头都空也照样给对象：那表示「不限日期」，同样要覆盖掉列表上的区间。
   */
  period?: ExportPeriod;
  /** 导出面板单独指定的付款状态；不给（或空串）＝跟随列表筛选 */
  paymentStatus?: string;
  /**
   * 只进文件名的范围标记（客户详情页的「未付款」那一项给「未付款」）。
   * 同一客户的全部 / 未付款两份台账如果同名，下载目录里只能靠修改时间猜哪份是欠款单——
   * 和期次标签不写进文件名是同一个 bug。
   */
  filenameScope?: string;
}

/** 面板塞进 `onExport` 的那一小份选择（不含客户维度，那两个键由客户详情页自己传） */
export type ExportSelection = Pick<ExportOptions, 'period' | 'paymentStatus'>;

/**
 * 导出与打印的取数参数：与列表页筛选逐字一致，只额外带上面板的三项（客户维度 / 期次 / 付款状态）。
 * 分页参数不带也不收——三个出口都在服务端一次取全量。
 *
 * 期次与付款状态在后端 query DTO 里**本来就有落点**（`dateFrom`/`dateTo`/`paymentStatus`），
 * 所以面板只是换一格填值，没有新筛选键、也没有「导出专用口径」。
 *
 * 状态那格同样要过 `toOrderStatusQuery`：三个出口共用后端一份 query DTO，
 * 分段名「在途」直接发过去会被 `@IsIn` 判成 400，而列表页看着正常、导出报错最难查。
 */
function toParams(filters: OrderFilters, options?: ExportOptions): Record<string, unknown> {
  const { orderStatus, ...rest } = filters;
  const params: Record<string, unknown> = { ...rest, ...toOrderStatusQuery(orderStatus) };
  if (options?.customerId) {
    params.customerId = options.customerId;
  }
  // 面板一旦接管日期（选了期次，或落在「自定义」档），就**整体替换**列表那两个日期框：
  // 只填一头时另一头是**清掉**而不是留着列表的值——否则「自定义起 2026-01-01」会悄悄和
  // 列表上的 dateTo 拼成一段谁也没选过的区间。
  // `upcoming`（「即将到来」）必须摘掉：它在服务端是有意再覆盖一次 dateFrom/dateTo 的，
  // 留着它，用户选「按 2025 年导出」拿回来的仍是未来 7 天。
  if (options?.period) {
    if (options.period.dateFrom) params.dateFrom = options.period.dateFrom;
    else delete params.dateFrom;
    if (options.period.dateTo) params.dateTo = options.period.dateTo;
    else delete params.dateTo;
    delete params.upcoming;
  }
  // 空串＝跟随筛选：列表页的付款状态本来就带在 filters 里，面板没另选时一个字不动。
  if (options?.paymentStatus) {
    params.paymentStatus = options.paymentStatus;
  }
  return params;
}

/**
 * 文件名里的日期补零（`2026-10-09`），与服务端 `订单列表_${businessDateStamp()}.xlsx` 同一写法。
 * 此前用 `toLocaleDateString('zh-CN')`，实测东八区给 `2026/10/9`：既不补零，
 * 同一批导出按文件名排序时 10 月会插到 1 月和 2 月之间。
 */
function buildFilename(base: string, ext: string, options?: ExportOptions): string {
  const prefix = options?.customerName ? `${options.customerName}_${base}` : base;
  // 期次插在日期之前：`订单列表_2026年三季度_2026-10-10.xlsx`。
  // 不写这一格，同一季分批导出几个文件同名，下载目录里只能靠修改时间猜各是哪期的账。
  const period = options?.period?.label ? `_${options.period.label}` : '';
  const scope = options?.filenameScope ? `_${options.filenameScope}` : '';
  return `${prefix}${period}${scope}_${todayString()}.${ext}`;
}

export async function exportOrdersXlsx(filters: OrderFilters, options?: ExportOptions): Promise<void> {
  // 台账由服务端产出（GET /orders/export/xlsx 挂 @RequireFeature('export_csv')）：
  // 与列表页共用同一套筛选、视角翻转与派生状态，且不受前端一次性拉数的上限约束。
  // 取的是字节——.xlsx 是 ZIP，走文本通道解一次就毁了。
  const bytes = await fetchAuthorizedBinary('/orders/export/xlsx', toParams(filters, options));
  if (bytes.byteLength === 0) throw new Error('导出失败');
  downloadBytes(buildFilename('订单列表', 'xlsx', options), bytes, XLSX_MIME);
}

/**
 * 导出 PDF 文件：服务端排版的真 .pdf（GET /orders/export/pdf 挂 @RequireFeature('export_print')）。
 * 与台账、打印文档同一份取数，呈现是台账那 22 列明细（打印才是 12 列摘要），
 * 按客户的导出会顶部给出客户信息块、表体拆成支出 / 收入两张表。
 *
 * 取的是字节——PDF 走文本通道解一次就毁了。
 */
export async function exportOrdersPdf(filters: OrderFilters, options?: ExportOptions): Promise<void> {
  const bytes = await fetchAuthorizedBinary('/orders/export/pdf', toParams(filters, options));
  if (bytes.byteLength === 0) throw new Error('导出失败');
  downloadBytes(buildFilename('订单列表', 'pdf', options), bytes, 'application/pdf');
}

/**
 * 打印：文档由服务端产出（GET /orders/export/print 挂 @RequireFeature('export_print')），
 * 筛选与投影和台账同源，只是呈现换成 12 列精简版。
 *
 * 取代此前「前端拉 2000 条 → 拼 DOM → html2canvas 拍成 2x 位图 → jsPDF 按固定高度切图」的管线：
 * 那条路产出的是整页图片（文字不可选、放大发虚、还会把表格行拦腰切断），
 * 且服务端全程没有落点，「尊享会员专属」只剩一个客户端勾选框。
 *
 * 这个入口只负责把表交给浏览器打印对话框（选纸张、份数、双面），不再兼职「拿 PDF 文件」——
 * 「另存为 PDF」是本机装了 PDF 虚拟打印机才有的选项，实测没有，于是导出 PDF 另走上面那个端点。
 */
export async function printOrdersDocument(filters: OrderFilters, options?: ExportOptions): Promise<void> {
  const html = await get<string>('/orders/export/print', { params: toParams(filters, options) });
  if (typeof html !== 'string' || !html.trim()) throw new Error('打印文档生成失败，请稍后重试');
  await printHtmlDocument(html);
}
